/**
 * VENDORED — do not edit the interceptor logic locally.
 *
 * Source: software-architect-ai/templates/api-axios-instance.ts
 *   commit 6519c8f9f5cb3f7e5988473adfc8176144a8f24c (authored 2026-06-20)
 *   vendored into dink-admin 2026-06-21 (DNKADM-0037).
 *
 * This is the canonical React-fleet axios instance factory. EXTEND it via
 * `createApiClient(config)` with repo-specific config — do NOT edit the four
 * interceptor bodies here. When the fleet template changes, RE-VENDOR this file
 * (copy the new template, update the source commit/date above) rather than
 * forking the interceptor logic. dink-admin's app-specific extras (conditional
 * Content-Type, the DNKADM-0034 `X-Session-Mode` admin-mutation header, and the
 * `statusToCode`/`ApiError` normalization in `src/api/errors.ts`) live in the
 * thin wrapper in `src/api/client.ts`, NOT in this file.
 *
 * Local deltas vs. the template (TYPE-only, NO behavior change — re-apply on
 * re-vendor): `override` on `ApiError.name`, a `?? ''` baseURL fallback, and a
 * conditionally-built error `details` object, all to satisfy dink-admin's
 * stricter tsconfig (`exactOptionalPropertyTypes`, `noImplicitOverride`).
 *
 * ---------------------------------------------------------------------------
 * CANONICAL axios instance factory — React fleet HTTP layer.
 * See software-architect-ai/http-client-strategy.md.
 *
 * EXTEND THIS, DON'T FORK IT. A repo creates its client by calling
 * `createApiClient(config)` with repo-specific endpoints, an in-memory token
 * store, path predicates, and (optionally) a tenant context. The four
 * interceptors (csrf / bearer / silent-refresh / error-normalize) are
 * centralized here — repos supply config, never copy interceptor logic.
 *
 * Pin: axios ^1.16.0. Tokens are IN MEMORY ONLY (no localStorage/sessionStorage).
 */
import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';

/* ---------------------------------------------------------------- error model */

/** Extend this union per repo with the backend's known codes. Unknown -> 'UNKNOWN'. */
export type ErrorCode =
  | 'SESSION_EXPIRED'
  | 'CSRF_VALIDATION_FAILED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN'
  | (string & {}); // allow repo-specific codes without losing autocomplete

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    readonly requestId?: string,
    readonly details: { message?: string; raw?: unknown } = {},
  ) {
    super(`API error: ${code}`);
  }
}

interface BackendEnvelope {
  success?: false;
  error?: { code?: string; message?: string };
}

/* --------------------------------------------------------------------- config */

export interface ApiClientConfig {
  /** Single base URL, OR a per-path resolver for multi-base surfaces (admin vs auth). */
  baseUrl?: string;
  resolveBaseUrl?: (path: string) => string;

  /** In-memory token store. The factory NEVER touches storage directly. */
  tokens: {
    getAccess: () => string | null;
    setAccess: (token: string | null, expiresAt?: number | null) => void;
    getCsrf: () => string | null;
    setCsrf: (token: string | null) => void;
  };

  /** Attach Authorization: Bearer on these paths. */
  bearer: { protected: (path: string) => boolean };

  /** CSRF policy. */
  csrf: {
    protected: (path: string, method: string) => boolean;
    /** 'cookie-echo' (same-origin auth surface) | 'store-token' (cross-origin surface). */
    mode: (path: string) => 'cookie-echo' | 'store-token';
    cookieName?: string; // default 'journix_csrf'
    fetchPath?: string; // default '/api/auth/csrf' (cookie-echo fallback)
  };

  /** Silent refresh endpoint (flat body: { accessToken, expiresAt?, csrfToken? }). */
  refresh: { path: string };

  /** Called when refresh fails — repo signs out / redirects to login. */
  onSessionExpired: () => void;

  /** OPTIONAL. Omit to rely on server-derived tenancy (the default). */
  getTenantContext?: () => { tenantId?: string } | null;

  /** Generate a request id (uuid). Repo supplies its own. */
  generateRequestId?: () => string;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/* --------------------------------------------------------------------- factory */

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const instance = axios.create({ withCredentials: true });
  const reqId = config.generateRequestId ?? (() => crypto.randomUUID());
  const cookieName = config.csrf.cookieName ?? 'journix_csrf';
  const csrfFetchPath = config.csrf.fetchPath ?? '/api/auth/csrf';

  let refreshInFlight: Promise<boolean> | null = null;
  let csrfFetchInFlight: Promise<string | null> | null = null;

  /* [1+2+3] one request interceptor, deterministic order: base+headers -> bearer -> csrf */
  instance.interceptors.request.use(async (req) => {
    const path = pathOf(req);
    const method = (req.method ?? 'get').toUpperCase();

    // [1] base URL + standard headers + optional tenant context
    req.baseURL = config.resolveBaseUrl ? config.resolveBaseUrl(path) : (config.baseUrl ?? '');
    req.headers.set('Accept', 'application/json');
    req.headers.set('X-Request-ID', reqId());
    const tenant = config.getTenantContext?.();
    if (tenant?.tenantId) req.headers.set('X-Tenant-ID', tenant.tenantId);

    // [2] bearer
    if (config.bearer.protected(path)) {
      const token = config.tokens.getAccess();
      if (token) req.headers.set('Authorization', `Bearer ${token}`);
    }

    // [3] csrf (two modes)
    if (config.csrf.protected(path, method) && MUTATING.has(method)) {
      if (config.csrf.mode(path) === 'cookie-echo') {
        const token = readCookie(cookieName) ?? (await fetchCsrf());
        if (token) {
          req.headers.set('X-CSRF-Token', token);
          if (isPlainObject(req.data)) req.data = { ...req.data, csrfToken: token }; // body echo
        }
      } else {
        const token = config.tokens.getCsrf(); // store-token (cross-origin): header only
        if (token) req.headers.set('X-CSRF-Token', token);
      }
    }
    return req;
  });

  /* [4a + 4b] one response interceptor: try refresh+retry, else normalize-and-throw */
  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError<BackendEnvelope>) => {
      const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
      const status = error.response?.status ?? 0;
      const backendCode = error.response?.data?.error?.code;
      const path = original ? pathOf(original) : '';
      const method = (original?.method ?? 'get').toUpperCase();

      // [4a] silent refresh + single retry (bounded by _retry)
      const refreshable =
        !!original &&
        !original._retry &&
        (status === 401 ||
          (status === 403 &&
            backendCode === 'INVALID_CSRF' &&
            config.csrf.mode(path) === 'store-token' &&
            MUTATING.has(method)));
      if (refreshable) {
        original._retry = true;
        const ok = await runRefresh();
        if (ok) return instance(original);
        config.onSessionExpired();
        throw new ApiError('SESSION_EXPIRED', 401, idOf(error));
      }

      // [4b] error normalization
      throw normalize(error, status, backendCode);
    },
  );

  /* ---- helpers ---- */

  async function runRefresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const { data } = await instance.post<{
          accessToken?: string;
          expiresAt?: number | null;
          csrfToken?: string | null;
        }>(config.refresh.path, {}, { headers: { 'X-Refresh': '1' } });
        if (!data?.accessToken) return false;
        config.tokens.setAccess(data.accessToken, data.expiresAt ?? null);
        config.tokens.setCsrf(data.csrfToken ?? null); // authoritative for store-token surfaces
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function fetchCsrf(): Promise<string | null> {
    if (csrfFetchInFlight) return csrfFetchInFlight;
    csrfFetchInFlight = instance
      .get<{ csrfToken?: string }>(csrfFetchPath)
      .then((r) => r.data?.csrfToken ?? null)
      .catch(() => null)
      .finally(() => {
        csrfFetchInFlight = null;
      });
    return csrfFetchInFlight;
  }

  return instance;
}

/* ----------------------------------------------------------- module helpers */

function normalize(error: AxiosError<BackendEnvelope>, status: number, backendCode?: string): ApiError {
  if (!error.response) {
    return new ApiError('NETWORK_ERROR', 0, idOf(error), { message: error.message });
  }
  const code: ErrorCode = backendCode ?? 'UNKNOWN';
  const details: { message?: string; raw?: unknown } = {};
  const message = error.response.data?.error?.message;
  if (message !== undefined) details.message = message;
  if (!backendCode) details.raw = error.response.data;
  return new ApiError(code, status, idOf(error), details);
}

function pathOf(req: { url?: string; baseURL?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

function idOf(error: AxiosError): string | undefined {
  const h = error.config?.headers?.['X-Request-ID'];
  return typeof h === 'string' ? h : undefined;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${esc}=([^;]+)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
